import { ComponentProps, PropsWithChildren } from 'react'
import classnames from 'classnames'

import {
    SortingIconButton,
    SortingOrder,
} from '../SortingIconButton/SortingIconButton'

import { getTableHeaderCellProps } from './utils/getTableHeaderCellProps'
import { Table } from '../..'

type TableHeaderCellProps = PropsWithChildren &
    Omit<
        ReturnType<typeof getTableHeaderCellProps>,
        'isHidden' | 'actualHeader'
    > & {
        sorting: SortingOrder
        isSticky?: boolean
        onClick?: () => void
    } & Pick<ComponentProps<typeof Table>, 'style'>

export const TableHeaderCell: React.FC<TableHeaderCellProps> = (props) => {
    const {
        id,
        isLeaf,
        isSticky,
        colSpan,
        rowSpan,
        onClick,
        sorting,
        children,
        canSort,
        style,
    } = props

    const thBaseClass = classnames(
        `column-${id}`,
        'height-[1px]', // allows div inside to have height: 100%
        'p-0',
        'text-heading-caps text-left align-bottom',
        'text-neutral-800',
        'border-b border-neutral-200',
        'text-ellipsis overflow-hidden whitespace-nowrap',
        {
            '[&:not(:last-child)_.table-cell-content]:pr-0':
                style === 'list' && id !== 'select',
            'border-r': style !== 'list',
        }
    )

    const thClassname = classnames(thBaseClass, {
        'sticky left-0 z-10': isSticky,
        'bg-neutral-200': !!sorting,
        'bg-neutral-50': !sorting,
        'w-[1px]': id === 'select',
    })

    const wrapperClassname = classnames(
        'group/sorting h-full grid grid-rows-[1fr,auto]',
        {
            'cursor-pointer': isLeaf && canSort,
        }
    )

    const contentClassname = classnames(
        'table-cell-content',
        'flex w-full items-start gap-2 px-4 py-2'
    )

    return (
        <th
            className={thClassname}
            colSpan={colSpan}
            rowSpan={rowSpan}
            data-testid={`${id}-th`}
        >
            <div className={wrapperClassname} onClick={onClick}>
                <div className="spacer"></div>
                <div className={contentClassname}>
                    {children}

                    {isLeaf && canSort && (
                        <SortingIconButton
                            name={`${id}-sorting-toggle`}
                            order={sorting}
                            groupControlledStyles={true}
                        />
                    )}
                </div>
            </div>
        </th>
    )
}
